import axios from "../../api/axiosInstance";
import moment from 'moment';
import { extractErrorInfo, STATUS } from "./sharedfunction";


export async function getCostAnalysisUsageInfo(filters = {}, page = 1, pageSize = 25) {
    try {
        const params = {};
        if (filters.organization && filters.organization.trim().length > 0) {
            params.organizationId = filters.organization;
        }
        if (filters.project && Array.isArray(filters.project) && filters.project.length > 0) {
            params.projectIds = filters.project.join(',');
        }
        if (filters.user && Array.isArray(filters.user) && filters.user.length > 0) {
            params.userIds = filters.user.join(',');
        }
        if (filters.modelversion && Array.isArray(filters.modelversion) && filters.modelversion.length > 0) {
            params.modelVersions = filters.modelversion.join(',');
        }
        if (filters.executionmode && Array.isArray(filters.executionmode) && filters.executionmode.length > 0) {
            params.executionModes = filters.executionmode.join(',');
        }
        if (filters.startDate && filters.startDate.trim().length > 0) {
            params.startDate = moment(filters.startDate).format('YYYY-MM-DD');
        }
        if (filters.endDate && filters.endDate.trim().length > 0) {
            params.endDate = moment(filters.endDate).format('YYYY-MM-DD');
        }
        // Add pagination parameters
        params.page = page;
        params.pageSize = pageSize;
        
        const response = await axios.get('/cost-tracking/fetch-usage-info', { params });
        if (response?.data?.success) {
            // Handle new nested response structure: data.data (array) and data.pagination
            const responseData = response?.data?.data || {};
            return {
              status: STATUS.SUCCESS,
              data: responseData.data || [], // Array of records
              pagination: responseData.pagination || {}, // Pagination info
              message: response?.data?.message,
            };
        }
      
        return {
            status: STATUS.ERROR,
            error: response?.data?.message || response?.data?.error,
            errors: response?.data?.errors,
        };
    } catch (error) {
        console.error("Error fetching cost analysis:", error);
        return extractErrorInfo(error);
    }
}

export async function getCostAnalysisOrgWiseData({ organization, modelVersions, executionModes, startDate, endDate }) {
    try {
        const params = {};
        if (organization && organization.trim().length > 0) {
            params.organizationId = organization;
        }
        if (modelVersions && Array.isArray(modelVersions) && modelVersions.length > 0) {
            params.modelVersions = modelVersions.join(',');
        }
        if (executionModes && Array.isArray(executionModes) && executionModes.length > 0) {
            params.executionModes = executionModes.join(',');
        }
        if (startDate && startDate.trim().length > 0) {
            params.startDate = moment(startDate).format('YYYY-MM-DD');
        }
        if (endDate && endDate.trim().length > 0) {
            params.endDate = moment(endDate).format('YYYY-MM-DD');
        }
        const response = await axios.get('/cost-tracking/fetch-organization-usage-info', { params });
        if (response?.data?.success) {
            return {
                status: STATUS.SUCCESS,
                data: response?.data?.data || {},
                message: response?.data?.message,
            };
        }
        return {
            status: STATUS.ERROR,
            error: response?.data?.message || response?.data?.error,
            errors: response?.data?.errors,
        };
    } catch (error) {
        console.error("Error fetching cost analysis org wise data:", error);
        return extractErrorInfo(error);
    }
}

export async function getCostAnalysisProjectUserUsageInfo({ organizationId, projectIds, userIds, modelVersions, executionModes, startDate, endDate }) {
    try{
        if (!organizationId || organizationId.trim().length === 0) {
            return {
                status: STATUS.ERROR,
                error: "Organization ID is required",
            };
        }
        const params = {};
        if (projectIds && Array.isArray(projectIds) && projectIds.length > 0) {
            params.projectIds = projectIds.join(',');
        }
        if (userIds && Array.isArray(userIds) && userIds.length > 0) {
            params.userIds = userIds.join(',');
        }
        if (modelVersions && Array.isArray(modelVersions) && modelVersions.length > 0) {
            params.modelVersions = modelVersions.join(',');
        }
        if (executionModes && Array.isArray(executionModes) && executionModes.length > 0) {
            params.executionModes = executionModes.join(',');
        }
        if (startDate && startDate.trim().length > 0) {
            params.startDate = moment(startDate).format('YYYY-MM-DD');
        }
        if (endDate && endDate.trim().length > 0) {
            params.endDate = moment(endDate).format('YYYY-MM-DD');
        }
        const response = await axios.get(`/cost-tracking/fetch-project-user-usage-info/${organizationId}`, { params });
        if (response?.data?.success) {
            return {
                status: STATUS.SUCCESS,
                data: response?.data?.data || {},
            };
        }
        return {
            status: STATUS.ERROR,
            error: response?.data?.message || response?.data?.error,
            errors: response?.data?.errors,
        };
    } catch (error) {
        console.error("Error fetching cost analysis project user usage info:", error);
        return extractErrorInfo(error);
    }
}

export async function fetchFilterList({ organizationId, projectIds }) {
    try {
        const response = await axios.get('/cost-tracking/fetch-filters-list', {
            params: {
                organizationId,
                projectIds,
            },
        });
        if (response?.data?.success) {
            return {
                status: STATUS.SUCCESS,
                data: response?.data?.data || {},
            };
        }
        return {
            status: STATUS.ERROR,
            error: response?.data?.message || response?.data?.error,
            errors: response?.data?.errors,
        };
    } catch (error) {
        console.error("Error fetching filter list:", error);
        return extractErrorInfo(error);
    }
}

export async function updateModelTokenCosts(costUpdates) {
    try {
        // Map costs array to costUpdates format expected by backend
        const formattedUpdates = Array.isArray(costUpdates) 
            ? costUpdates.map(cost => ({
                id: cost.id,
                inputTokensCost: cost.inputTokensCost !== undefined ? cost.inputTokensCost : undefined,
                outputTokensCost: cost.outputTokensCost !== undefined ? cost.outputTokensCost : undefined
            }))
            : costUpdates;
        
        const response = await axios.post('/cost-tracking/update-llm-cost', { costUpdates: formattedUpdates });
        if (response?.data?.success) {
            return {
                status: STATUS.SUCCESS,
                data: response?.data?.data || {},
                message: response?.data?.message,
            };
        }
        return {
            status: STATUS.ERROR,
            error: response?.data?.message || response?.data?.error,
        };
    } catch (error) {
        console.error("Error updating model costs:", error);
        return extractErrorInfo(error);
    }
}

export async function fetchLLMCostList() {
    try {
        const response = await axios.get('/cost-tracking/fetch-llm-cost-list');
        if (response?.data?.success) {
            return {
                status: STATUS.SUCCESS,
                data: response?.data?.data || [],
                message: response?.data?.message,
            };
        }
        return {
            status: STATUS.ERROR,
            error: response?.data?.message || response?.data?.error,
            errors: response?.data?.errors,
        };
    } catch (error) {
        console.error("Error fetching LLM cost list:", error);
        return extractErrorInfo(error);
    }
}

export async function updateLLMCost(costUpdates) {
    try {
        const response = await axios.post('/cost-tracking/update-llm-cost', { costUpdates });
        if (response?.data?.success) {
            return {
                status: STATUS.SUCCESS,
                data: response?.data?.data || {},
                message: response?.data?.message,
            };
        }
        return {
            status: STATUS.ERROR,
            error: response?.data?.message || response?.data?.error,
            errors: response?.data?.errors,
        };
    } catch (error) {
        console.error("Error updating LLM costs:", error);
        return extractErrorInfo(error);
    }
}

export async function downloadCostTrackingCSV(filters = {}) {
    try {
        const params = {};
        if (filters.organization && filters.organization.trim().length > 0) {
            params.organizationId = filters.organization;
        }
        if (filters.project && Array.isArray(filters.project) && filters.project.length > 0) {
            params.projectIds = filters.project.join(',');
        }
        if (filters.user && Array.isArray(filters.user) && filters.user.length > 0) {
            params.userIds = filters.user.join(',');
        }
        if (filters.modelversion && Array.isArray(filters.modelversion) && filters.modelversion.length > 0) {
            params.modelVersions = filters.modelversion.join(',');
        }
        if (filters.executionmode && Array.isArray(filters.executionmode) && filters.executionmode.length > 0) {
            params.executionModes = filters.executionmode.join(',');
        }
        if (filters.startDate && filters.startDate.trim().length > 0) {
            params.startDate = moment(filters.startDate).format('YYYY-MM-DD');
        }
        if (filters.endDate && filters.endDate.trim().length > 0) {
            params.endDate = moment(filters.endDate).format('YYYY-MM-DD');
        }

        const response = await axios.get('/cost-tracking/download-csv', { 
            params,
            responseType: 'blob' // Important for file downloads
        });

        // Validate Content-Type header to ensure it's CSV (defensive check)
        const contentType = response.headers['content-type'] || '';
        if (!contentType.includes('text/csv') && !contentType.includes('application/csv')) {
            // If content-type is JSON, it's likely an error response that got through
            if (contentType.includes('application/json')) {
                // Clone the blob before reading to avoid consuming the original
                const blobClone = response.data.slice();
                const errorText = await blobClone.text();
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                } catch (e) {
                    errorData = { message: 'Invalid response format from server' };
                }
                
                return {
                    status: STATUS.ERROR,
                    error: errorData.message || errorData.error || 'Server returned an error instead of CSV file',
                    errors: errorData.errors,
                    statusCode: response.status
                };
            }
            
            // Log warning but proceed if status is 2xx (might be a backend configuration issue)
            console.warn(`Unexpected content type for CSV download: ${contentType}`);
        }

        // Create blob URL and trigger download
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Extract filename from Content-Disposition header if available
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'cost-tracking.csv';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return {
            status: STATUS.SUCCESS,
            message: 'CSV downloaded successfully',
        };
    } catch (error) {
        // Handle blob error responses
        if (error.response && error.response.data instanceof Blob) {
            try {
                const errorText = await error.response.data.text();
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                } catch (e) {
                    // If not JSON, use the text as error message
                    errorData = { message: errorText || 'Failed to download CSV' };
                }
                
                return {
                    status: STATUS.ERROR,
                    error: errorData.message || errorData.error || 'Failed to download CSV',
                    errors: errorData.errors,
                    statusCode: error.response.status
                };
            } catch (blobError) {
                // If we can't read the blob, fall through to default error handling
                console.error("Error reading blob error response:", blobError);
            }
        }
        
        // Default error handling for non-blob errors
        console.error("Error downloading cost tracking CSV:", error);
        return extractErrorInfo(error);
    }
}